import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from '../components/ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { DOMParser as PMMDOMParser } from '@tiptap/pm/model';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import api from '../utils/api';
import dayjs from 'dayjs';
import { ArrowLeft, Trash2, WifiOff, Users, Tag } from 'lucide-react';
import EditorToolbar from '../components/EditorToolbar';
import MarkdownPaste from '../extensions/MarkdownPaste';
import { markdownToHtml } from '../utils/markdownUtils';
import FileAttachments from '../components/FileAttachments';
import { getContrastColor } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';

const COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6'];

function getUserColor(id) {
  return COLORS[(id || 0) % COLORS.length];
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

// 아바타 컴포넌트
function Avatar({ admin, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-sm';
  const color = COLORS[(admin.id || 0) % COLORS.length];
  if (admin.avatarUrl) {
    return (
      <img src={admin.avatarUrl} alt={admin.name}
        className={`${sizeClass} rounded-full object-cover border-2 border-gray-950 flex-shrink-0`} />
    );
  }
  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-bold border-2 border-gray-950 flex-shrink-0`}
      style={{ backgroundColor: color, color: '#fff' }}>
      {admin.name?.charAt(0)}
    </div>
  );
}

// 참가자 편집 드롭다운
function ParticipantDropdown({ participants, noteId, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [selected, setSelected] = useState([]);
  const dropRef = useRef(null);

  useEffect(() => {
    setSelected(participants.map(p => p.adminId).filter(Boolean));
  }, [participants]);

  const load = () => api.get('/meeting-notes/admins-list').then(r => setAdmins(r.data || []));

  useEffect(() => { if (open) load(); }, [open]);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = async (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    setSelected(next);
    await api.put(`/meeting-notes/${noteId}/participants`, { adminIds: next });
    onUpdated();
  };

  return (
    <div className="relative" ref={dropRef}>
      <button onClick={() => setOpen(o => !o)}
        className="flex -space-x-2 hover:opacity-80 transition-opacity items-center">
        {participants.slice(0, 4).map(p => p.admin && (
          <Avatar key={p.adminId} admin={p.admin} />
        ))}
        {participants.length === 0 && (
          <div className="w-7 h-7 rounded-full border-2 border-dashed border-gray-600 flex items-center justify-center">
            <Users size={12} className="text-gray-500" />
          </div>
        )}
        {participants.length > 4 && <span className="text-xs text-gray-500 ml-1.5">+{participants.length - 4}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-8 bg-gray-900 border border-gray-700 rounded-xl p-3 w-52 z-50 shadow-xl" onClick={e => e.stopPropagation()}>
          <p className="text-gray-400 text-xs mb-2">참가자</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {admins.map(a => {
              const color = COLORS[(a.id || 0) % COLORS.length];
              const checked = selected.includes(a.id);
              return (
                <div key={a.id} onClick={() => toggle(a.id)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-brand/15' : 'hover:bg-gray-800'}`}>
                  {a.avatarUrl
                    ? <img src={a.avatarUrl} className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                    : <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ backgroundColor: color, color: '#fff' }}>{a.name?.charAt(0)}</div>
                  }
                  <span className="text-white text-xs flex-1">{a.name}</span>
                  {checked && <span className="text-brand text-xs">✓</span>}
                </div>
              );
            })}
          </div>
          <button onClick={() => setOpen(false)} className="mt-2 w-full text-center text-xs text-gray-500 hover:text-white">닫기</button>
        </div>
      )}
    </div>
  );
}

// 카테고리 선택 드롭다운
function CategorySelector({ categoryId, noteId, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [current, setCurrent] = useState(null);
  const dropRef = useRef(null);

  const loadCategories = () =>
    api.get('/meeting-notes/categories').then(r => setCategories(r.data || []));

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      setCurrent(categories.find(c => c.id === categoryId) || null);
    }
  }, [categoryId, categories]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const select = async (cat) => {
    const newId = cat?.id ?? null;
    await api.put(`/meeting-notes/${noteId}`, { categoryId: newId });
    setCurrent(cat || null);
    setOpen(false);
    onUpdated();
  };

  return (
    <div className="relative" ref={dropRef}>
      <button
        onClick={() => { if (!open) loadCategories(); setOpen(o => !o); }}
        className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
        style={current ? { borderColor: current.color + '60', color: current.color } : {}}
      >
        {current ? (
          <>
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: current.color }} />
            <span>{current.name}</span>
          </>
        ) : (
          <>
            <Tag size={11} className="text-gray-500" />
            <span className="text-gray-500">카테고리</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-8 bg-gray-900 border border-gray-700 rounded-xl p-2 w-44 z-50 shadow-xl" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => select(null)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors ${!current ? 'bg-brand/15 text-brand' : 'text-gray-400 hover:bg-gray-800'}`}
          >
            <Tag size={11} />
            없음
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => select(cat)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors ${current?.id === cat.id ? 'bg-gray-700' : 'hover:bg-gray-800'}`}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
              <span className="text-gray-200">{cat.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MeetingNoteEditorPage() {
  const { theme } = useTheme();
  const avatarColor = (color) => theme === 'light' ? getContrastColor(color) : 'white';
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isAdmin = user.role === 'superadmin' || user.role === 'admin';

  const [note, setNote] = useState(null);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const fileInputRef = useRef(null);
  const mdFileInputRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const titleSaveTimer = useRef(null);
  const contentSaveTimer = useRef(null);
  const saveStatusTimer = useRef(null);

  // Yjs 문서 + Provider
  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const token = localStorage.getItem('mfg_token');
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const isLocal = ['localhost', '127.0.0.1', '100.116.73.97'].includes(window.location.hostname);
    const wsHost = isLocal ? `${window.location.hostname}:3800` : window.location.host;
    const prov = new WebsocketProvider(`${wsProtocol}//${wsHost}/yjs`, `meeting-${id}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
  }, [id]);

  const ytitle = useMemo(() => ydoc.getText('title'), [ydoc]);

  const loadNote = useCallback(() =>
    api.get(`/meeting-notes/${id}`).then(r => setNote(r.data)), [id]);

  useEffect(() => { loadNote(); }, [loadNote]);

  // Provider 이벤트
  useEffect(() => {
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    if (provider.wsconnected) setConnected(true);

    provider.awareness.setLocalStateField('user', {
      name: user.name || user.username,
      color: getUserColor(user.id),
    });

    const awarenessHandler = () => {
      const seen = new Set();
      const users = [];
      provider.awareness.getStates().forEach(state => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ ...state.user, isMe: false });
        }
      });
      const myName = user.name || user.username;
      if (!seen.has(myName)) {
        users.unshift({ name: myName, color: getUserColor(user.id), isMe: true });
      } else {
        const idx = users.findIndex(u => u.name === myName);
        if (idx >= 0) { users[idx].isMe = true; if (idx > 0) users.unshift(users.splice(idx, 1)[0]); }
      }
      setPeers(users);
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();

    return () => { provider.destroy(); ydoc.destroy(); };
  }, [provider, ydoc]);

  // 제목 동기화
  const [title, setTitle] = useState('');

  useEffect(() => {
    const update = () => {
      const val = ytitle.toString();
      setTitle(val);
      if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
      titleSaveTimer.current = setTimeout(async () => {
        if (!val) return;
        setSaveStatus('saving');
        try {
          await api.put(`/meeting-notes/${id}`, { title: val });
          setSaveStatus('saved');
          clearTimeout(saveStatusTimer.current);
          saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
        } catch { setSaveStatus('error'); }
      }, 1000);
    };
    ytitle.observe(update);

    const onSync = (isSynced) => { if (isSynced) setTitle(ytitle.toString()); };
    provider.on('synced', onSync);
    if (provider.synced) setTitle(ytitle.toString());

    return () => {
      ytitle.unobserve(update);
      provider.off('synced', onSync);
      if (titleSaveTimer.current) clearTimeout(titleSaveTimer.current);
    };
  }, [ytitle, provider, id]);

  const handleTitleChange = (e) => {
    const val = e.target.value;
    ydoc.transact(() => {
      ytitle.delete(0, ytitle.length);
      if (val) ytitle.insert(0, val);
    });
  };

  // Yjs extension for Tiptap
  const YjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment('content');
      return [
        ySyncPlugin(fragment),
        yCursorPlugin(provider.awareness, {
          cursorBuilder: (user) => {
            const cursor = document.createElement('span');
            cursor.className = 'yjs-cursor';
            cursor.style.borderColor = user.color;
            const label = document.createElement('span');
            label.className = 'yjs-cursor-label';
            label.style.backgroundColor = user.color;
            label.textContent = user.name;
            cursor.appendChild(label);
            return cursor;
          },
        }),
        yUndoPlugin(),
      ];
    },
  }), [ydoc, provider]);

  // Tiptap 에디터
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExtension,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      MarkdownPaste,
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    autofocus: false,
    onUpdate: ({ editor }) => {
      setSaveStatus('saving');
      if (contentSaveTimer.current) clearTimeout(contentSaveTimer.current);
      contentSaveTimer.current = setTimeout(async () => {
        try {
          await api.put(`/meeting-notes/${id}`, { content: editor.getHTML() });
          setSaveStatus('saved');
          clearTimeout(saveStatusTimer.current);
          saveStatusTimer.current = setTimeout(() => setSaveStatus(null), 2000);
        } catch { setSaveStatus('error'); }
      }, 2000);
    },
    editorProps: {
      attributes: { class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-6 py-4' },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files?.length) {
          event.preventDefault();
          Array.from(files).forEach(async (f) => {
            if (!f.type.startsWith('image/')) return;
            const url = await uploadImage(f);
            view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
          });
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
            })();
            return true;
          }
        }
        return false;
      },
    },
  }, [ydoc]);

  // Plaud 승인으로 생성된 노트: ydoc=null이라 YJS fragment가 비어있음
  // editor와 note 모두 준비되면 YJS fragment가 비어있을 때 HTML content로 초기화
  useEffect(() => {
    if (!editor || !note?.content) return;
    const init = () => {
      const frag = ydoc.getXmlFragment('content');
      if (frag.length === 0) {
        editor.commands.setContent(note.content, false);
      }
    };
    if (provider.synced) {
      init();
    } else {
      provider.once('synced', init);
      return () => provider.off('synced', init);
    }
  }, [editor, note, provider, ydoc]);

  const handleImageButton = () => fileInputRef.current?.click();
  const handleMdUpload = () => mdFileInputRef.current?.click();
  const handleMdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const text = await file.text();
    const html = markdownToHtml(text);
    const view = editor.view;
    const parser = PMMDOMParser.fromSchema(view.state.schema);
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const slice = parser.parseSlice(dom, { preserveWhitespace: false });
    const tr = view.state.tr.replaceSelection(slice);
    view.dispatch(tr.scrollIntoView());
    e.target.value = '';
  };
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const handleDelete = async () => {
    if (!confirm('이 회의록을 삭제하시겠습니까?')) return;
    await api.delete(`/meeting-notes/${id}`);
    navigate('/work/meeting');
  };

  const canDelete = note?.createdBy === user.id || isAdmin;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-950 flex-shrink-0 gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button onClick={() => navigate('/work/meeting')}
            className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800 flex-shrink-0">
            <ArrowLeft size={18} />
          </button>
          <input value={title} onChange={handleTitleChange}
            placeholder="제목을 입력하세요"
            className="flex-1 bg-transparent text-white text-lg font-bold focus:outline-none placeholder-gray-600 min-w-0" />
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* 참가자 드롭다운 */}
          {note && (
            <ParticipantDropdown
              participants={note.participants || []}
              noteId={id}
              onUpdated={loadNote}
            />
          )}

          {/* 접속자 */}
          <div className="flex -space-x-1.5">
            {peers.slice(0, 5).map((p, i) => (
              <div key={i}
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${p.isMe ? 'border-brand' : 'border-gray-950'}`}
                style={{ backgroundColor: p.color, color: avatarColor(p.color) }}
                title={p.isMe ? `${p.name} (나)` : p.name}>
                {p.name?.charAt(0)}
              </div>
            ))}
          </div>

          {saveStatus === 'saving' && <span className="text-gray-500 text-xs">저장 중...</span>}
          {saveStatus === 'saved' && <span className="text-green-500 text-xs">저장됨</span>}
          {saveStatus === 'error' && <span className="text-red-400 text-xs">저장 실패</span>}
          {!connected && (
            <div className="flex items-center gap-1 text-red-400 text-xs"><WifiOff size={14} /> 오프라인</div>
          )}

          {canDelete && (
            <button onClick={handleDelete} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-gray-800">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* 메타 */}
      {note && (
        <div className="px-6 py-2 text-xs text-gray-500 border-b border-gray-800/50 flex-shrink-0 flex items-center gap-3">
          <span>{note.author?.name}</span>
          <span>{dayjs(note.createdAt).format('YYYY.MM.DD HH:mm')}</span>
          <CategorySelector
            categoryId={note.categoryId}
            noteId={id}
            onUpdated={loadNote}
          />
        </div>
      )}

      {/* 에디터 */}
      <div className="flex-1 flex flex-col min-h-0">
        <EditorToolbar editor={editor} onImageUpload={handleImageButton} onMarkdownUpload={handleMdUpload} />
        <div className="flex-1 overflow-y-auto">
          <EditorContent editor={editor} />
        </div>
        <FileAttachments entityType="meeting" entityId={note?.id} editor={editor} />
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
        <input ref={mdFileInputRef} type="file" accept=".md,text/markdown" className="hidden" onChange={handleMdFile} />
      </div>
    </div>
  );
}
